/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useEffect, useRef } from 'react';

import { SearchState, SearchFieldConfig } from '@/types/search';
import { getTodayDateString } from '@/utils/date-time';

export const useSearch = (
  fields: SearchFieldConfig[],
  onChange?: (params: Record<string, any>) => void,
  initialValues?: Record<string, any>
) => {
  // Initialize state with default values or initial values
  // If isClearAll is true, skip initialValues and only use defaultValue (for clearAll)
  // Otherwise, use initialValues if provided, otherwise use defaultValue
  const getInitialState = useCallback(
    (isClearAll: boolean = false): SearchState => {
      const values: Record<string, any> = {};
      const errors: Record<string, string> = {};
      const touched: Record<string, boolean> = {};

      for (const field of fields) {
        if (isClearAll) {
          // Clear case: ignore initialValues, use defaultValue if provided, otherwise reset to empty string
          values[field.key] = field.defaultValue ?? '';
        } else {
          // Normal case: use initialValues if provided, otherwise use defaultValue
          values[field.key] = initialValues?.[field.key] ?? field.defaultValue ?? '';
        }
        errors[field.key] = '';
        touched[field.key] = false;
      }

      return { values, errors, touched };
    },
    [fields, initialValues]
  );

  const [state, setState] = useState<SearchState>(() => {
    const initialState = getInitialState();
    return initialState;
  });

  const onChangeRef = useRef(onChange);
  const [isInitialized, setIsInitialized] = useState(false);
  const isRestoringFromInitialValuesRef = useRef(false); // Track if we're restoring from initialValues
  const previousInitialValuesRef = useRef<string>(''); // Store serialized initialValues to detect actual changes

  // Update state when initialValues change (e.g., when navigating back)
  // But don't trigger onChange callback to avoid infinite loop
  useEffect(() => {
    if (!initialValues || Object.keys(initialValues).length === 0) {
      return;
    }

    // Serialize initialValues to compare with previous value
    const serializedInitialValues = JSON.stringify(initialValues);

    // Only update if values actually changed (not just reference)
    if (serializedInitialValues === previousInitialValuesRef.current) {
      return;
    }

    previousInitialValuesRef.current = serializedInitialValues;

    isRestoringFromInitialValuesRef.current = true; // Mark that we're restoring
    setState((prev) => {
      const newValues: Record<string, any> = {};
      for (const field of fields) {
        newValues[field.key] =
          initialValues[field.key] ?? prev.values[field.key] ?? field.defaultValue ?? '';
      }
      return {
        ...prev,
        values: newValues,
      };
    });
    // Reset flag after state update
    setTimeout(() => {
      isRestoringFromInitialValuesRef.current = false;
    }, 0);
  }, [initialValues, fields]);

  // Update onChange ref when it changes
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Update field value
  const updateField = useCallback((key: string, value: any) => {
    setState((prev) => {
      const newState = {
        ...prev,
        values: {
          ...prev.values,
          [key]: value,
        },
        touched: {
          ...prev.touched,
          [key]: true,
        },
      };

      return newState;
    });
  }, []);

  // Call onChange when state changes (but not on initial render or when restoring from initialValues)
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }

    // Don't trigger onChange if we're restoring from initialValues (to avoid infinite loop)
    if (isRestoringFromInitialValuesRef.current) {
      return;
    }

    if (onChangeRef.current) {
      const params: Record<string, any> = {};
      for (const [key, value] of Object.entries(state.values)) {
        if (value !== '' && value !== null && value !== undefined) {
          params[key] = value;
        }
      }
      onChangeRef.current(params);
    }
  }, [state.values, isInitialized]);

  // Validate single field
  const validateField = useCallback(
    (_key: string, value: any, fieldConfig: SearchFieldConfig): string => {
      if (fieldConfig.required && (!value || value === '')) {
        return `${fieldConfig.label}は必須です`;
      }

      if (fieldConfig.validation) {
        return fieldConfig.validation(value) || '';
      }

      return '';
    },
    []
  );

  // Validate all fields
  const validateAll = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    for (const field of fields) {
      const error = validateField(field.key, state.values[field.key], field);
      if (error) {
        newErrors[field.key] = error;
        isValid = false;
      }
    }

    setState((prev) => ({
      ...prev,
      errors: newErrors,
      touched: Object.keys(prev.values).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    }));

    return isValid;
  }, [fields, state.values, validateField]);

  // Clear all fields - reset to empty values, ignoring initialValues/defaultValue
  const clearAll = useCallback(() => {
    setState(getInitialState(true));
  }, [getInitialState]);

  // Get search parameters (only non-empty values)
  const getSearchParams = useCallback(() => {
    const params: Record<string, any> = {};

    for (const [key, value] of Object.entries(state.values)) {
      if (value !== '' && value !== null && value !== undefined) {
        params[key] = value;
      }
    }

    return params;
  }, [state.values]);

  // Check if form has any values
  const hasValues = useCallback(() => {
    const today = getTodayDateString();

    return fields.some((field) => {
      const value = state.values[field.key];

      // Plans force-case: treat today's 予定日 as default so Clear stays disabled.
      if (field.key === 'placementDateFrom' && field.label === '予定日') {
        return value !== today;
      }

      if (value === '' || value === null || value === undefined) {
        return false;
      }

      return true;
    });
  }, [fields, state.values]);

  return {
    state,
    updateField,
    validateAll,
    clearAll,
    getSearchParams,
    hasValues,
  };
};
